import { StepConfig } from "./StepperHelpers";
import { createStore, useStore } from "zustand";
import { createContext, useContext } from "react";

export interface StepperProps {
    activeStep?: number;
    startingStep?: number;
    totalSteps: number;
    stepConfigs: StepConfig[];
}

export interface StepperState extends StepperProps {
    slideDirection: "up" | "down";
    enterDirection: "up" | "down";
    setSlideDirection: (direction: "up" | "down") => void;
    setEnterDirection: (direction: "up" | "down") => void;
    setStartingStepIndex: (index: number) => void;
    goToStep: (index: number) => void;
    goToNextStep: () => void;
    goBackOneStep: () => void;
    setStepConfigs: (configs: StepConfig[]) => void;
    setTotalSteps: (steps: number) => void;
    resetStepper: () => void;
    getHasNextStep: () => boolean;
    getHasPreviousStep: () => boolean;
}

export type StepperStore = ReturnType<typeof createStepperStore>;

export const createStepperStore = (initProps?: Partial<StepperProps>) => {
    const DEFAULT_PROPS: StepperProps = {
        stepConfigs: [],
        activeStep: 0,
        startingStep: 0,
        totalSteps: 1,
    };
    const mergedProps = {
        ...DEFAULT_PROPS,
        ...initProps,
    };
    if (initProps?.startingStep !== undefined && initProps?.activeStep === undefined) {
        mergedProps.activeStep = initProps.startingStep;
    }
    return createStore<StepperState>()((set, get) => ({
        ...mergedProps,
        slideDirection: "up",
        enterDirection: "up",
        setSlideDirection: (direction: "up" | "down") => {
            set({ slideDirection: direction });
        },
        setEnterDirection: (direction: "up" | "down") => {
            set({ enterDirection: direction });
        },
        setStartingStepIndex: (index: number) => {
            set({
                startingStep: index,
                activeStep: index,
            });
        },
        goToStep: (index: number) => {
            set({ activeStep: index });
        },
        goToNextStep: () => {
            set({
                activeStep: get().activeStep + 1,
            });
        },
        goBackOneStep: () => {
            set({
                activeStep: get().activeStep - 1,
            });
        },
        setStepConfigs: (configs: StepConfig[]) => {
            set({
                stepConfigs: configs,
                totalSteps: configs?.length || 0,
            });
        },
        setTotalSteps: (steps: number) => {
            set({
                totalSteps: steps,
            });
        },
        resetStepper: () => {
            set({
                activeStep: get().startingStep,
            });
        },
        getHasNextStep: () => get().activeStep !== get().totalSteps - 1,
        getHasPreviousStep: () => get().activeStep - 1 >= get().startingStep,
    }));
};
export const StepperContext = createContext<StepperStore | null>(null);

export const useStepperContext = <T,>(selector: (state: StepperState) => T, equalityFn?: (left: T, right: T) => boolean): T => {
    const store = useContext(StepperContext);
    if (!store) throw new Error("Missing StepperContext.Provider in the tree");
    return useStore(store, selector, equalityFn);
};
